import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Grid,
  Tabs,
  Tab,
  alpha,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Rating,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import TodayIcon from '@mui/icons-material/Today';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DescriptionIcon from '@mui/icons-material/Description';
import NotesIcon from '@mui/icons-material/Notes';
import TimerIcon from '@mui/icons-material/Timer';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import PageLayout from './PageLayout';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ptBR from 'date-fns/locale/pt-BR';
import {
  format,
  startOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  isWithinInterval,
} from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

const DEFAULT_PRIMARY = '#1976d2';

const MATERIAL_TYPES = [
  { id: 'livro', label: 'Livro' },
  { id: 'curso', label: 'Curso' },
  { id: 'artigo', label: 'Artigo' },
  { id: 'podcast', label: 'Podcast' },
  { id: 'outro', label: 'Outro' },
];

const MATERIAL_STATUS = [
  { id: 'nao_iniciado', label: 'Não iniciado' },
  { id: 'em_andamento', label: 'Em andamento' },
  { id: 'concluido', label: 'Concluído' },
];

function getDateKey(date) {
  return format(startOfDay(date), 'yyyy-MM-dd');
}

export default function Estudos({ customColors }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const primaryColor = customColors?.primary || DEFAULT_PRIMARY;

  const [activeTab, setActiveTab] = useState(0);

  const [materials, setMaterials] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [notes, setNotes] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  // Sessão de estudo
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [isSessionRunning, setIsSessionRunning] = useState(false);
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [sessionStart, setSessionStart] = useState(null);
  const sessionTimerRef = useRef(null);

  // Materiais dialog
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialTitle, setMaterialTitle] = useState('');
  const [materialType, setMaterialType] = useState('livro');
  const [materialAuthor, setMaterialAuthor] = useState('');
  const [materialStatus, setMaterialStatus] = useState('nao_iniciado');
  const [materialRating, setMaterialRating] = useState(0);

  // Anotações
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteMaterialId, setNoteMaterialId] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteDate, setNoteDate] = useState(new Date());

  const [historyViewMode, setHistoryViewMode] = useState('week'); // week | month

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) return;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          setMaterials(data.studyMaterials || []);
          setSessions((data.studySessions || []).map((s) => ({
            ...s,
            durationMinutes: Number(s.durationMinutes) || 0,
          })));
          setNotes(data.studyNotes || []);
        }
      } catch (err) {
        console.error('Erro ao carregar dados de estudos:', err);
      }
    };
    load();

    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, [currentUser?.uid]);

  const saveMaterials = async (next) => {
    if (!currentUser?.uid) return;
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, { studyMaterials: next });
    setMaterials(next);
  };

  const saveSessions = async (next) => {
    if (!currentUser?.uid) return;
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, { studySessions: next });
    setSessions(next);
  };

  const saveNotes = async (next) => {
    if (!currentUser?.uid) return;
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, { studyNotes: next });
    setNotes(next);
  };

  const todayKey = getDateKey(new Date());

  const stats = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

    const weeklySessions = sessions.filter((s) => {
      if (!s.date) return false;
      const d = new Date(s.date + 'T12:00:00');
      return isWithinInterval(d, { start: weekStart, end: weekEnd });
    });

    const totalWeekMinutes = weeklySessions.reduce(
      (sum, s) => sum + (Number(s.durationMinutes) || 0),
      0,
    );

    const dates = Array.from(
      new Set(sessions.map((s) => s.date).filter(Boolean)),
    );
    let streak = 0;
    for (let i = 0; i < 365; i++) {
      const key = getDateKey(subDays(now, i));
      if (dates.includes(key)) streak += 1;
      else break;
    }

    const todayNotes = notes.filter((n) => n.date === todayKey);

    return {
      totalMaterials: materials.length,
      totalWeekMinutes,
      streak,
      totalNotes: notes.length,
      todayNotesCount: todayNotes.length,
    };
  }, [materials, sessions, notes, todayKey]);

  const studyTimeByDay = useMemo(() => {
    const now = new Date();
    const days = Array.from({ length: 7 }).map((_, idx) =>
      subDays(now, 6 - idx),
    );
    return days.map((d) => {
      const key = getDateKey(d);
      const minutes = sessions
        .filter((s) => s.date === key)
        .reduce((sum, s) => sum + (Number(s.durationMinutes) || 0), 0);
      return {
        day: format(d, 'dd/MM', { locale: ptBR }),
        minutes,
      };
    });
  }, [sessions]);

  const getMaterialTitle = (id, fallback) => {
    if (!id) return fallback || 'Sem material';
    const mat = materials.find((m) => m.id === id);
    return mat?.title || fallback || 'Sem material';
  };

  const handleStartSession = () => {
    if (!selectedMaterialId && !customSubject.trim()) {
      setSnackbar({
        open: true,
        message: 'Selecione um material ou informe o assunto.',
        severity: 'warning',
      });
      return;
    }
    if (isSessionRunning) return;
    setIsSessionRunning(true);
    setSessionSeconds(0);
    setSessionStart(new Date().toISOString());
    sessionTimerRef.current = setInterval(() => {
      setSessionSeconds((prev) => prev + 1);
    }, 1000);
  };

  const handleStopSession = async () => {
    if (!isSessionRunning) return;
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
    setIsSessionRunning(false);

    const minutes = Math.max(1, Math.round(sessionSeconds / 60));
    const dateKey = todayKey;

    const newSession = {
      id: `study-${Date.now()}`,
      date: dateKey,
      materialId: selectedMaterialId || null,
      customSubject: selectedMaterialId ? '' : customSubject.trim(),
      durationMinutes: minutes,
      startedAt: sessionStart,
      endedAt: new Date().toISOString(),
    };
    try {
      const next = [...sessions, newSession];
      await saveSessions(next);
      setSnackbar({
        open: true,
        message: 'Sessão de estudo registrada.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar sessão.',
        severity: 'error',
      });
    }
  };

  const handleOpenMaterialDialog = (material) => {
    if (material) {
      setEditingMaterial(material);
      setMaterialTitle(material.title || '');
      setMaterialType(material.type || 'livro');
      setMaterialAuthor(material.author || '');
      setMaterialStatus(material.status || 'nao_iniciado');
      setMaterialRating(Number(material.rating) || 0);
    } else {
      setEditingMaterial(null);
      setMaterialTitle('');
      setMaterialType('livro');
      setMaterialAuthor('');
      setMaterialStatus('nao_iniciado');
      setMaterialRating(0);
    }
    setMaterialDialogOpen(true);
  };

  const handleSaveMaterial = async () => {
    if (!materialTitle.trim()) {
      setSnackbar({
        open: true,
        message: 'Informe um título para o material.',
        severity: 'warning',
      });
      return;
    }
    setLoading(true);
    try {
      let next;
      if (editingMaterial) {
        next = materials.map((m) =>
          m.id === editingMaterial.id
            ? {
                ...m,
                title: materialTitle.trim(),
                type: materialType,
                author: materialAuthor.trim(),
                status: materialStatus,
                rating: materialRating,
                updatedAt: new Date().toISOString(),
              }
            : m,
        );
      } else {
        const newMaterial = {
          id: `mat-${Date.now()}`,
          title: materialTitle.trim(),
          type: materialType,
          author: materialAuthor.trim(),
          status: materialStatus,
          rating: materialRating,
          createdAt: new Date().toISOString(),
        };
        next = [...materials, newMaterial];
      }
      await saveMaterials(next);
      setMaterialDialogOpen(false);
      setEditingMaterial(null);
      setSnackbar({
        open: true,
        message: 'Material salvo com sucesso.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar material.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMaterial = async (material) => {
    if (!window.confirm('Remover este material e suas ligações com sessões/anotações?')) {
      return;
    }
    try {
      const nextMaterials = materials.filter((m) => m.id !== material.id);
      await saveMaterials(nextMaterials);
      setSessions((prev) =>
        prev.map((s) =>
          s.materialId === material.id ? { ...s, materialId: null } : s,
        ),
      );
      setNotes((prev) =>
        prev.map((n) =>
          n.materialId === material.id ? { ...n, materialId: '' } : n,
        ),
      );
      setSnackbar({
        open: true,
        message: 'Material removido.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao remover material.',
        severity: 'error',
      });
    }
  };

  const handleOpenNoteDialog = (note) => {
    if (note) {
      setEditingNote(note);
      setNoteTitle(note.title || '');
      setNoteMaterialId(note.materialId || '');
      setNoteContent(note.content || '');
      setNoteDate(
        note.date
          ? new Date(note.date + 'T12:00:00')
          : new Date(),
      );
    } else {
      setEditingNote(null);
      setNoteTitle('');
      setNoteMaterialId('');
      setNoteContent('');
      setNoteDate(new Date());
    }
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!noteTitle.trim() || !noteContent.trim()) {
      setSnackbar({
        open: true,
        message: 'Preencha título e conteúdo da anotação.',
        severity: 'warning',
      });
      return;
    }
    setLoading(true);
    const dateKey = getDateKey(noteDate || new Date());
    try {
      let next;
      if (editingNote) {
        next = notes.map((n) =>
          n.id === editingNote.id
            ? {
                ...n,
                title: noteTitle.trim(),
                materialId: noteMaterialId || '',
                content: noteContent.trim(),
                date: dateKey,
                updatedAt: new Date().toISOString(),
              }
            : n,
        );
      } else {
        const newNote = {
          id: `note-${Date.now()}`,
          title: noteTitle.trim(),
          materialId: noteMaterialId || '',
          content: noteContent.trim(),
          date: dateKey,
          createdAt: new Date().toISOString(),
        };
        next = [...notes, newNote];
      }
      await saveNotes(next);
      setNoteDialogOpen(false);
      setEditingNote(null);
      setSnackbar({
        open: true,
        message: 'Anotação salva com sucesso.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar anotação.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (note) => {
    if (!window.confirm('Remover esta anotação?')) return;
    try {
      const next = notes.filter((n) => n.id !== note.id);
      await saveNotes(next);
      setSnackbar({
        open: true,
        message: 'Anotação removida.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao remover anotação.',
        severity: 'error',
      });
    }
  };

  const filteredSessions = useMemo(() => {
    const now = new Date();
    if (historyViewMode === 'week') {
      const start = subDays(now, 6);
      const end = now;
      return sessions.filter((s) => {
        if (!s.date) return false;
        const d = new Date(s.date + 'T12:00:00');
        return isWithinInterval(d, { start, end });
      });
    }
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = now;
    return sessions.filter((s) => {
      if (!s.date) return false;
      const d = new Date(s.date + 'T12:00:00');
      return isWithinInterval(d, { start, end });
    });
  }, [sessions, historyViewMode]);

  return (
    <PageLayout title="Estudos" customColors={customColors} hideAddButton>
      <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
        <Card
          sx={{
            mb: 2,
            background: `linear-gradient(135deg, ${alpha(primaryColor, 0.12)} 0%, ${alpha(
              primaryColor,
              0.04,
            )} 100%)`,
            borderLeft: `4px solid ${primaryColor}`,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 600 },
              '& .Mui-selected': { color: primaryColor },
              '& .MuiTabs-indicator': { backgroundColor: primaryColor },
            }}
          >
            <Tab icon={<TodayIcon />} iconPosition="start" label="Dashboard" />
            <Tab icon={<SchoolIcon />} iconPosition="start" label="Sessão de estudo" />
            <Tab icon={<DescriptionIcon />} iconPosition="start" label="Materiais" />
            <Tab icon={<NotesIcon />} iconPosition="start" label="Anotações" />
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Histórico" />
          </Tabs>
        </Card>

        {activeTab === 0 && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Materiais cadastrados
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor }}>
                      {stats.totalMaterials}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Tempo de estudo na semana
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor }}>
                      {Math.round(stats.totalWeekMinutes / 60 * 10) / 10} h
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Streak de estudo
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor }}>
                      {stats.streak}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Anotações de estudo
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor }}>
                      {stats.totalNotes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card sx={{ borderLeft: `4px solid ${primaryColor}` }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>
                  Tempo de estudo por dia (últimos 7 dias)
                </Typography>
                {!studyTimeByDay.some((d) => d.minutes > 0) ? (
                  <Typography variant="body2" color="text.secondary">
                    Registre sessões de estudo para visualizar o gráfico.
                  </Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={studyTimeByDay} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="minutes"
                        fill={primaryColor}
                        name="Minutos"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {activeTab === 1 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 2 }}>
                Sessão de estudo
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Material de estudo"
                    value={selectedMaterialId}
                    onChange={(e) => setSelectedMaterialId(e.target.value)}
                    helperText="Opcional: selecione um material já cadastrado"
                  >
                    <MenuItem value="">Nenhum (assunto livre)</MenuItem>
                    {materials.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Assunto / material (texto livre)"
                    fullWidth
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    helperText="Use quando o material não estiver cadastrado"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {Math.floor(sessionSeconds / 60)
                    .toString()
                    .padStart(2, '0')}
                  :
                  {(sessionSeconds % 60).toString().padStart(2, '0')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tempo corrente da sessão
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStartSession}
                  disabled={isSessionRunning}
                  sx={{ bgcolor: primaryColor }}
                >
                  Iniciar sessão
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<StopIcon />}
                  onClick={handleStopSession}
                  disabled={!isSessionRunning || sessionSeconds === 0}
                  sx={{ borderColor: primaryColor, color: primaryColor }}
                >
                  Encerrar e registrar
                </Button>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Timer Pomodoro
                        </Typography>
                        <TimerIcon color="primary" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Use o Timer Pomodoro para estruturar sua sessão de estudo em ciclos de foco e
                        pausas.
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/pomodoro')}
                        sx={{ borderColor: primaryColor, color: primaryColor }}
                      >
                        Abrir Timer Pomodoro
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Player de áudio/vídeo
                        </Typography>
                        <HeadphonesIcon color="primary" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Use o Player para reproduzir vídeos/áudios de foco (lo-fi, sons ambientes,
                        etc.) durante seus estudos.
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/player')}
                        sx={{ borderColor: primaryColor, color: primaryColor }}
                      >
                        Abrir Player
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                  Materiais de estudo
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenMaterialDialog(null)}
                  sx={{ bgcolor: primaryColor }}
                >
                  Novo material
                </Button>
              </Box>

              {materials.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum material cadastrado ainda.
                </Typography>
              ) : (
                <List dense>
                  {materials.map((m) => (
                    <ListItem
                      key={m.id}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleOpenMaterialDialog(m)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleDeleteMaterial(m)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <>
                            {m.title}{' '}
                            {m.rating ? (
                              <Rating
                                size="small"
                                value={Number(m.rating) || 0}
                                precision={0.5}
                                readOnly
                              />
                            ) : null}
                          </>
                        }
                        secondary={`${MATERIAL_TYPES.find((t) => t.id === m.type)?.label || m.type}${
                          m.author ? ` · ${m.author}` : ''
                        } · ${
                          MATERIAL_STATUS.find((s) => s.id === m.status)?.label || m.status
                        }`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 3 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                  Anotações de estudo
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenNoteDialog(null)}
                  sx={{ bgcolor: primaryColor }}
                >
                  Nova anotação
                </Button>
              </Box>
              {notes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma anotação de estudo ainda.
                </Typography>
              ) : (
                <List dense>
                  {notes
                    .slice()
                    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                    .map((n) => {
                      const labelDate = n.date
                        ? format(new Date(n.date + 'T12:00:00'), 'dd/MM/yyyy', {
                            locale: ptBR,
                          })
                        : '';
                      return (
                        <ListItem
                          key={n.id}
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleOpenNoteDialog(n)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                size="small"
                                color="error"
                                onClick={() => handleDeleteNote(n)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={`${labelDate} — ${n.title}`}
                            secondary={getMaterialTitle(n.materialId, 'Sem material vinculado')}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 4 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                  Histórico de estudo
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={historyViewMode}
                  exclusive
                  onChange={(_, v) => v && setHistoryViewMode(v)}
                >
                  <ToggleButton value="week">Últimos 7 dias</ToggleButton>
                  <ToggleButton value="month">Este mês</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {filteredSessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma sessão registrada no período selecionado.
                </Typography>
              ) : (
                <List dense>
                  {filteredSessions
                    .slice()
                    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                    .map((s) => {
                      const labelDate = s.date
                        ? format(new Date(s.date + 'T12:00:00'), 'dd/MM/yyyy', {
                            locale: ptBR,
                          })
                        : '';
                      const labelMaterial =
                        s.materialId || s.customSubject
                          ? getMaterialTitle(s.materialId, s.customSubject)
                          : 'Sessão livre';
                      return (
                        <ListItem key={s.id}>
                          <ListItemText
                            primary={`${labelDate} — ${labelMaterial}`}
                            secondary={`${s.durationMinutes} min de estudo`}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Dialog Material */}
      <Dialog
        open={materialDialogOpen}
        onClose={() => {
          setMaterialDialogOpen(false);
          setEditingMaterial(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingMaterial ? 'Editar material' : 'Novo material'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Título"
              fullWidth
              value={materialTitle}
              onChange={(e) => setMaterialTitle(e.target.value)}
            />
            <TextField
              select
              fullWidth
              label="Tipo"
              value={materialType}
              onChange={(e) => setMaterialType(e.target.value)}
            >
              {MATERIAL_TYPES.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Autor (opcional)"
              fullWidth
              value={materialAuthor}
              onChange={(e) => setMaterialAuthor(e.target.value)}
            />
            <TextField
              select
              fullWidth
              label="Status"
              value={materialStatus}
              onChange={(e) => setMaterialStatus(e.target.value)}
            >
              {MATERIAL_STATUS.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Avaliação
              </Typography>
              <Rating
                value={materialRating}
                precision={0.5}
                onChange={(_, v) => setMaterialRating(v || 0)}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMaterialDialogOpen(false);
              setEditingMaterial(null);
            }}
          >
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveMaterial} disabled={loading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Anotação */}
      <Dialog
        open={noteDialogOpen}
        onClose={() => {
          setNoteDialogOpen(false);
          setEditingNote(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingNote ? 'Editar anotação' : 'Nova anotação'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Título"
              fullWidth
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data"
                value={noteDate}
                onChange={(d) => setNoteDate(d || new Date())}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
            <TextField
              select
              fullWidth
              label="Material relacionado"
              value={noteMaterialId}
              onChange={(e) => setNoteMaterialId(e.target.value)}
            >
              <MenuItem value="">Nenhum</MenuItem>
              {materials.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Conteúdo da anotação"
              fullWidth
              multiline
              minRows={3}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNoteDialogOpen(false);
              setEditingNote(null);
            }}
          >
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveNote} disabled={loading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}

